import { BaseService } from './base.service';
import { db } from '../data_oscbase';
import { PaymentService } from './payment.service';

export class SaleService extends BaseService {
  constructor() {
    super('sales');
    this.paymentService = new PaymentService();
  }

  /**
   * Récupère les ventes dans une période donnée
   * @param {Date} startDate - Date de début
   * @param {Date} endDate - Date de fin
   * @param {Object} filters - Filtres optionnels
   * @param {string} filters.status - Filtre par statut ('draft', 'pending', 'completed', 'cancelled', 'refunded')
   * @param {string} filters.paymentMethod - Filtre par méthode de paiement ('cash', 'card', 'transfer', 'check', 'mobile')
   * @param {string} filters.paymentStatus - Filtre par statut de paiement ('pending', 'partial', 'paid')
   * @param {number} filters.customerId - Filtre par ID client
   * @returns {Promise<Array>} - Liste des ventes dans la période
   */
  async getSales(startDate, endDate, filters = {}) {
    try {
      if (!startDate || !endDate) {
        throw new Error('Les dates de début et de fin sont obligatoires');
      }

      let query = this.table;

      // Appliquer le filtre de période
      query = query.where('date').between(startDate, endDate);

      // Appliquer les filtres optionnels
      if (filters.status) {
        query = query.and(sale => sale.status === filters.status);
      }

      if (filters.paymentMethod) {
        query = query.and(sale => sale.paiement.method === filters.paymentMethod);
      }

      if (filters.paymentStatus) {
        query = query.and(sale => sale.paiement.status === filters.paymentStatus);
      }

      if (filters.customerId) {
        query = query.and(sale => sale.customerId === filters.customerId);
      }

      const sales = await query.toArray();
      return sales;
    } catch (error) {
      console.error('Erreur lors de la récupération des ventes:', error);
      throw error;
    }
  }

  /**
   * Calcule le total des ventes sur une période
   * @param {Date} startDate - Date de début
   * @param {Date} endDate - Date de fin
   * @returns {Promise<number>} - Total des ventes
   */
  async getTotalSalesByPeriod(startDate, endDate) {
    try {
      const sales = await this.getSales(startDate, endDate);
      return sales.reduce((total, sale) => total + sale.montant.total, 0);
    } catch (error) {
      console.error('Erreur lors du calcul du total des ventes:', error);
      throw error;
    }
  }

  /**
   * Crée une nouvelle vente
   * @param {Object} saleData - Données de la vente
   * @returns {Promise<Object>} - Vente créée
   */
  async createSale(saleData) {
    try {
      // Générer le numéro de vente si non fourni
      if (!saleData.num) {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        // Récupérer le dernier numéro de vente du jour
        const todaySales = await this.table
          .where('num')
          .startsWith(`V${year}${month}${day}`)
          .toArray();
        
        const lastNum = todaySales.length > 0 
          ? Math.max(...todaySales.map(s => parseInt(s.num.split('-')[1])))
          : 0;
        
        saleData.num = `V${year}${month}${day}-${String(lastNum + 1).padStart(3, '0')}`;
      }

      // S'assurer que la date est définie
      if (!saleData.date) {
        saleData.date = new Date();
      }

      // S'assurer que le sens est défini (1 pour vente, -1 pour retour)
      if (!saleData.sens) {
        saleData.sens = 1;
      }

      // Initialiser les dates de création et mise à jour
      saleData.createdAt = new Date();
      saleData.updatedAt = new Date();

      const newSale = await this.create(saleData);
      return newSale;
    } catch (error) {
      console.error('Erreur lors de la création de la vente:', error);
      throw error;
    }
  }

  /**
   * Crée une nouvelle vente avec son paiement associé dans une transaction atomique
   * @param {Object} saleData - Données de la vente
   * @param {Object} paymentData - Données du paiement (optionnel)
   * @returns {Promise<Object>} - Vente créée avec les informations de paiement
   */
  async createSaleWithPayment(saleData, paymentData = null) {
    try {
      // Utiliser une transaction Dexie pour garantir l'atomicité
      return await db.transaction('rw', [this.table, this.paymentService.table], async () => {
        // 1. Créer la vente
        const savedSale = await this.createSale(saleData);
        
        // 2. Créer le paiement si des données de paiement sont fournies
        let savedPayment = null;
        if (paymentData && paymentData.amount > 0) {
          const paymentRecord = {
            saleId: savedSale.id,
            userId: paymentData.userId || 1,
            amount: paymentData.amount,
            method: paymentData.method || 'cash',
            type: paymentData.type || 'payment',
            status: paymentData.status || 'completed',
            reference: paymentData.reference || `PAY-${savedSale.num}`,
            notes: paymentData.notes || '',
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          savedPayment = await this.paymentService.createPayment(paymentRecord);
        }
        
        // 3. Retourner les données complètes
        return {
          sale: savedSale,
          payment: savedPayment,
          success: true
        };
      });
      
    } catch (error) {
      console.error('Erreur lors de la création de la vente avec paiement:', error);
      
      // En cas d'erreur, on peut essayer de nettoyer si nécessaire
      // (Dexie gère automatiquement le rollback de la transaction)
      
      throw new Error(`Échec de la transaction vente/paiement: ${error.message}`);
    }
  }

  /**
   * Met à jour une vente existante
   * @param {number} saleId - ID de la vente
   * @param {Object} updates - Données à mettre à jour
   * @returns {Promise<Object>} - Vente mise à jour
   */
  async updateSale(saleId, updates) {
    try {
      // Mettre à jour la date de modification
      updates.updatedAt = new Date();
      
      const updatedSale = await this.update(saleId, updates);
      return updatedSale;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la vente:', error);
      throw error;
    }
  }

  /**
   * Supprime une vente
   * @param {number} saleId - ID de la vente à supprimer
   * @returns {Promise<boolean>} - true si supprimée avec succès
   */
  async deleteSale(saleId) {
    try {
      // Vérifier les dépendances
      const payments = await db.payments.where('saleId').equals(saleId).toArray();
      if (payments.length > 0) {
        throw new Error('Impossible de supprimer la vente car elle a des paiements associés');
      }

      // Vérifier si la vente existe
      const sale = await this.getById(saleId);
      if (!sale) {
        throw new Error('Vente non trouvée');
      }

      // Vérifier si la vente peut être supprimée (statut)
      if (sale.status === 'completed' || sale.status === 'cancelled') {
        throw new Error('Impossible de supprimer une vente terminée ou annulée');
      }

      await this.delete(saleId);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la vente:', error);
      throw error;
    }
  }
} 